import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Search, MoreHorizontal, Pencil, UserMinus, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const VIEWS = [["", "All", "all"], ["contacts", "My Contacts", "contacts"], ["team", "My Team", "team"]];
const FIELDS = [["first_name", "First name *"], ["last_name", "Last name *"], ["email", "Email"], ["phone", "Phone"], ["company", "Company / Business"]];

function ContactDialog({ contact, onClose, onSaved }) {
  const isNew = !contact.id;
  const lockId = !isNew && !contact.identity_editable;
  const [f, setF] = useState({ first_name: contact.first_name || "", last_name: contact.last_name || "", email: contact.email || "",
    phone: contact.mobile || contact.phone || "", company: contact.company || "", notes: contact.notes || "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, (v || "").trim()]));
    try {
      if (isNew) await api.post("/agency/contacts", body);
      else await api.patch(`/agency/contacts/${contact.id}`, lockId ? { company: body.company, notes: body.notes } : body);
      toast.success(isNew ? "Contact added" : "Contact saved");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="contact-dialog">
        <DialogHeader><DialogTitle>{isNew ? "Add Contact" : "Edit Contact"}</DialogTitle>
          <DialogDescription>A contact is just someone you keep in touch with. No login, invitation or email is sent.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l]) => (
            <label key={k} className={`block ${k === "company" ? "sm:col-span-2" : ""}`}><span className="text-xs font-semibold text-slate-500">{l}</span>
              <Input className="mt-1" disabled={lockId && k !== "company"} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}`} /></label>))}
          <label className="block sm:col-span-2"><span className="text-xs font-semibold text-slate-500">Notes (optional)</span>
            <Textarea rows={3} className="mt-1" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="contact-notes" /></label>
        </div>
        {lockId && <p className="text-xs text-slate-500" data-testid="contact-identity-locked">This person's name, email and phone are managed elsewhere in Top-Recruit. You can update the company and your notes.</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !f.first_name.trim() || !f.last_name.trim()} className="bg-[#006090] hover:bg-[#004E75]" data-testid="contact-save">{saving ? "Saving…" : isNew ? "Add Contact" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>);
}

function RowActions({ p, onEdit, onRemove, onTeam }) {
  const contact = p.can_manage_contact;
  const team = p.agency_category === "My Team";
  if (!contact && !team) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <button className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-testid={`agent-people-actions-${p.id}`} aria-label="Actions"><MoreHorizontal className="w-4 h-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        {contact && <DropdownMenuItem onClick={onEdit} data-testid={`contact-edit-${p.id}`}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>}
        {contact && <DropdownMenuItem onClick={onRemove} className="text-rose-600" data-testid={`contact-remove-${p.id}`}><UserMinus className="w-4 h-4 mr-2" />Remove from My Contacts</DropdownMenuItem>}
        {team && <DropdownMenuItem onClick={onTeam} data-testid={`people-manage-team-${p.id}`}><Users className="w-4 h-4 mr-2" />Manage in My Team</DropdownMenuItem>}
      </DropdownMenuContent>
    </DropdownMenu>);
}

export default function AgentPeople() {
  const navigate = useNavigate();
  const { viewAs } = useAuth();
  const [view, setView] = useState("");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const load = useCallback(() => {
    api.get("/persons", { params: { agency_view: view, q, page_size: 100, sort: "last_name", order: "asc" } })
      .then((r) => { setRows(r.data.persons); setTotal(r.data.total); }).catch((e) => toast.error(apiError(e)));
    api.get("/persons/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, [view, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const remove = async () => {
    try { await api.delete(`/agency/contacts/${removing.id}`); toast.success("Removed from My Contacts"); setRemoving(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="max-w-[1200px] mx-auto" data-testid="agent-people">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div><h1 className="font-display text-3xl font-semibold text-slate-900">My Contacts</h1>
          <p className="text-slate-500 mt-1">Your Agency's people, your own contacts, and the shared directory.</p></div>
        {!viewAs && <Button onClick={() => setEditing({})} className="bg-[#006090] hover:bg-[#004E75]" data-testid="agent-people-add"><Plus className="w-4 h-4 mr-1" />Add Contact</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {VIEWS.map(([k, l, c]) => (
          <button key={k || "all"} onClick={() => setView(k)} data-testid={`agent-people-view-${c}`}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${view === k ? "bg-[#006090] text-white border-[#006090]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>
            {l} <span className="opacity-70">({k ? counts[k] ?? 0 : counts.all ?? 0})</span></button>))}
        <div className="relative w-full sm:w-auto sm:ml-auto"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" className="pl-8 w-full sm:w-64" data-testid="agent-people-search" /></div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm"><thead className="bg-slate-50 text-xs text-slate-500"><tr>
          {["Name", "Email", "Phone", "Category", ""].map((h, i) => <th key={i} className="text-left p-3">{h}</th>)}</tr></thead>
          <tbody>{(rows || []).map((p) => (
            <tr key={p.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/people/detail/${p.id}`)} data-testid={`agent-people-row-${p.id}`}>
              <td className="p-3 font-medium text-slate-900">{p.first_name} {p.last_name}{p.company ? <span className="block text-xs font-normal text-slate-400">{p.company}</span> : null}</td>
              <td className="p-3">{p.email || "\u2014"}</td>
              <td className="p-3">{p.mobile || p.phone || "\u2014"}</td><td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{p.agency_category}</span></td>
              <td className="p-3 w-10 text-right">{!viewAs && <RowActions p={p} onEdit={() => setEditing(p)} onRemove={() => setRemoving(p)} onTeam={() => navigate("/my-team")} />}</td>
            </tr>))}</tbody></table>
        {rows && rows.length === 0 && <p className="p-8 text-center text-slate-500" data-testid="agent-people-empty">No people here yet.</p>}
      </div>
      <p className="text-xs text-slate-400 mt-2" data-testid="agent-people-total">{total} {total === 1 ? "person" : "people"}</p>
      {editing && <ContactDialog contact={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="bg-white" data-testid="contact-remove-dialog">
          <AlertDialogHeader><AlertDialogTitle>Remove {removing?.first_name} {removing?.last_name} from My Contacts?</AlertDialogTitle>
            <AlertDialogDescription>This removes them from your contact list only. Their Top-Recruit record isn't deleted and no one is notified.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-700" data-testid="contact-remove-confirm">Remove</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>);
}
